import { FieldType, MeasurementUnits, TableType, getEntityName } from '../../../../shared/enums';
import {
    RequiredFieldValidator,
    RangeFieldValidator,
    CustomFieldValidator
} from '../../../../shared/zoneiqui/field-validators';
import fieldDictionary from '../dictionary/calibration-general.dictionary';
import officeCommercialFieldDictionary from '../dictionary/calibration-office-commercial.dictionary';
import * as _ from 'lodash';
import { FieldSettingsModel } from '../../common-models/field-settings.model';
import { ITableFieldsConfiguration } from 'src/app/interfaces/tables/table-fields-configuration.interface';
import { NA_VALUE } from 'src/app/constants';

enum GeneralSections {
    BUILDING_CONFIGURATION,
    LOT_OCCUPATION,
    SETBACKS_PRINCIPAL,
    SETBACKS_OUTBUILDING,
    SETBACKS_TOWER,
    SETBACKS_PENTHOUSE,
    SETBACKS_UNDERGROUND,
    PRIVATE_FRONTAGES
}

const sections = [
    'Building Configuration',
    'Lot Occupation',
    'Setbacks - Principal',
    'Setbacks - Outbuilding',
    'Setbacks - Tower',
    'Setbacks - Penthouse',
    'Setbacks - Underground',
    'Private Frontages'
];

const hasValue = (value: number | null | undefined): boolean => !_.isNil(value);

function isMinGreaterThanMax(
    model: { [key: string]: number | null | undefined },
    minFieldName: string,
    maxFieldName: string
): boolean {
    return hasValue(model[minFieldName]) && hasValue(model[maxFieldName]) && model[minFieldName] > model[maxFieldName];
}

function calibrationGeneralFieldsFactory() {
    return [
        // Building Configuration Section
        new FieldSettingsModel('PrincipalMaxHeightLvl', FieldType.NUMBER_NA, MeasurementUnits.LEVELS, {
            validators: [
                new RequiredFieldValidator(),
                new CustomFieldValidator(
                    `PrincipalMaxHeightLvl Value can't be more than ${fieldDictionary.TowerMaxHeightLvl.label}`,
                    ({ currentModelState }) =>
                        currentModelState.TowerMaxHeightLvl !== null &&
                        currentModelState.TowerMaxHeightLvl !== undefined &&
                        currentModelState.TowerMaxHeightLvl >= 0
                            ? currentModelState.PrincipalMaxHeightLvl <= currentModelState.TowerMaxHeightLvl
                            : true
                ),
                new CustomFieldValidator(
                    `PrincipalMaxHeightLvl Value can't be less than ${officeCommercialFieldDictionary.OfficeMaxStories.label}`,
                    ({ currentModelState, calibrationSet }) =>
                        !(
                            currentModelState.PrincipalMaxHeightLvl > currentModelState.TowerMaxHeightLvl &&
                            currentModelState.PrincipalMaxHeightLvl > currentModelState.PenthouseMaxHeightLvl &&
                            currentModelState.PrincipalMaxHeightLvl <
                                calibrationSet[getEntityName(TableType.CALIBRATION_USE_CAPACITY)].currentState
                                    .OfficeMaxStories
                        )
                ),
                // eslint-disable-next-line max-len
                new CustomFieldValidator(
                    `PrincipalMaxHeightLvl Value can't be less than ${officeCommercialFieldDictionary.OfficeCommercialMaxStories.label} (Combined from Sidewalk level)`,
                    ({ currentModelState, calibrationSet }) =>
                        !(
                            currentModelState.PrincipalMaxHeightLvl > currentModelState.TowerMaxHeightLvl &&
                            currentModelState.PrincipalMaxHeightLvl > currentModelState.PenthouseMaxHeightLvl &&
                            currentModelState.PrincipalMaxHeightLvl <
                                calibrationSet[getEntityName(TableType.CALIBRATION_USE_CAPACITY)].currentState
                                    .OfficeCommercialMaxStories
                        )
                ),
                new RangeFieldValidator(NA_VALUE, 500)
            ],
            isRequired: true,
            section: GeneralSections.BUILDING_CONFIGURATION,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('PrincipalBuildingMin', FieldType.NUMBER, MeasurementUnits.LEVELS, {
            validators: [new RangeFieldValidator(0, 500)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('PrincipalMaxHeightFt', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0, 5000),
                new CustomFieldValidator(
                    `PrincipalMaxHeightFt Value can't be empty if ${fieldDictionary.PrincipalMaxHeightLvl.label} is N/A`,
                    ({ currentModelState }) =>
                        !(currentModelState.PrincipalMaxHeightFt <= 0 && currentModelState.PrincipalMaxHeightLvl < 0)
                )
            ],
            section: GeneralSections.BUILDING_CONFIGURATION,
            isRelatedToRequired: true
        }),
        new FieldSettingsModel('PrincipalMinHeightFt', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0, 5000)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('Outbuilding', FieldType.NUMBER, MeasurementUnits.LEVELS, {
            validators: [new RangeFieldValidator(0, 10)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('ActivePrimaryFrontageGround', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('ActivePrimaryFrontageAbove', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('ActiveSecondaryFrontageGround', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('ActiveSecondaryFrontageAbove', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('ActiveWaterFrontageGround', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('ActiveWaterFrontageAbove', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('FacadePrimaryFrontage', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('FacadeSecondaryFrontage', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('PkgFacadeWidthMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0, 100)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('TowerMaxHeightLvl', FieldType.NUMBER, MeasurementUnits.LEVELS, {
            validators: [
                new RangeFieldValidator(NA_VALUE, 500),
                new CustomFieldValidator(
                    `TowerMaxHeightLvl Value can't be less than ${fieldDictionary.PrincipalMaxHeightLvl.label}`,
                    ({ currentModelState }) =>
                        currentModelState.TowerMaxHeightLvl !== null &&
                        currentModelState.TowerMaxHeightLvl !== undefined &&
                        currentModelState.TowerMaxHeightLvl >= 0
                            ? currentModelState.PrincipalMaxHeightLvl <= currentModelState.TowerMaxHeightLvl
                            : true
                ),
                new CustomFieldValidator(
                    `TowerMaxHeightLvl Value can't be less than ${officeCommercialFieldDictionary.OfficeMaxStories.label}`,
                    ({ currentModelState, calibrationSet }) =>
                        currentModelState.TowerMaxHeightLvl === null ||
                        currentModelState.TowerMaxHeightLvl === undefined ||
                        !(
                            currentModelState.PrincipalMaxHeightLvl <= currentModelState.TowerMaxHeightLvl &&
                            currentModelState.TowerMaxHeightLvl > currentModelState.PenthouseMaxHeightLvl &&
                            currentModelState.TowerMaxHeightLvl <
                                calibrationSet[getEntityName(TableType.CALIBRATION_USE_CAPACITY)].currentState
                                    .OfficeMaxStories
                        )
                ),
                // eslint-disable-next-line max-len
                new CustomFieldValidator(
                    `TowerMaxHeightLvl Value can't be less than ${officeCommercialFieldDictionary.OfficeCommercialMaxStories.label} (Combined from Sidewalk level)`,
                    ({ currentModelState, calibrationSet }) =>
                        currentModelState.TowerMaxHeightLvl === null ||
                        currentModelState.TowerMaxHeightLvl === undefined ||
                        !(
                            currentModelState.PrincipalMaxHeightLvl <= currentModelState.TowerMaxHeightLvl &&
                            currentModelState.TowerMaxHeightLvl > currentModelState.PenthouseMaxHeightLvl &&
                            currentModelState.TowerMaxHeightLvl <
                                calibrationSet[getEntityName(TableType.CALIBRATION_USE_CAPACITY)].currentState
                                    .OfficeCommercialMaxStories
                        )
                )
            ],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('PenthouseMaxHeightLvl', FieldType.NUMBER, MeasurementUnits.LEVELS, {
            validators: [
                new RangeFieldValidator(NA_VALUE, 500),
                new CustomFieldValidator(
                    `PenthouseMaxHeightLvl Value can't be less than ${fieldDictionary.PrincipalMaxHeightLvl.label}`,
                    ({ currentModelState }) => {
                        return !(
                            currentModelState.PenthouseMaxHeightLvl &&
                            currentModelState.PenthouseMaxHeightLvl > 0 &&
                            currentModelState.TowerMaxHeightLvl < currentModelState.PrincipalMaxHeightLvl &&
                            currentModelState.PrincipalMaxHeightLvl > currentModelState.PenthouseMaxHeightLvl
                        );
                    }
                ),
                new CustomFieldValidator(
                    `PenthouseMaxHeightLvl Value can't be less than ${fieldDictionary.TowerMaxHeightLvl.label}`,
                    ({ currentModelState }) => {
                        return !(
                            currentModelState.PenthouseMaxHeightLvl &&
                            currentModelState.PenthouseMaxHeightLvl > 0 &&
                            currentModelState.TowerMaxHeightLvl >= 0 &&
                            currentModelState.PrincipalMaxHeightLvl &&
                            currentModelState.TowerMaxHeightLvl > currentModelState.PenthouseMaxHeightLvl
                        );
                    }
                )
            ],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),
        new FieldSettingsModel('TowerMaxHeightFt', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0, 5000)],
            section: GeneralSections.BUILDING_CONFIGURATION
        }),

        // Lot Occupation Section
        new FieldSettingsModel('LotAreaMin', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('LotAreaMax', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('LotAreaMinAlley', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('LotAreaMaxAlley', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('LotWidthMin', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('LotWidthMinAlley', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('LotWidthMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(NA_VALUE, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('GreenSpace', FieldType.NUMBER_NA, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(NA_VALUE, 100), new RequiredFieldValidator()],
            section: GeneralSections.LOT_OCCUPATION,
            isRequired: true,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('LotCoverageFirstFloor', FieldType.NUMBER_NA, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(NA_VALUE, 100), new RequiredFieldValidator()],
            section: GeneralSections.LOT_OCCUPATION,
            isRequired: true,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('LotCoverageSecondFloor', FieldType.NUMBER, MeasurementUnits.PERCENT, {
            validators: [new RangeFieldValidator(0, 100)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('TowerFloorplateAreaResidential', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('TowerFloorplateAreaCommercial', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('PenthouseFloorPlate', FieldType.NUMBER, MeasurementUnits.SQUARE_FEET, {
            validators: [new RangeFieldValidator(0, 1000000)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('SecondLayerDimension', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.LOT_OCCUPATION
        }),
        new FieldSettingsModel('FLR', FieldType.NUMBER, MeasurementUnits.NONE, {
            validators: [new RangeFieldValidator(NA_VALUE)],
            section: GeneralSections.LOT_OCCUPATION
        }),

        // Setbacks - Principal Section
        new FieldSettingsModel('PFrontSetbackPrincipal', FieldType.NUMBER_NA, MeasurementUnits.FEET, {
            isRequired: true,
            validators: [
                new RequiredFieldValidator(),
                new RangeFieldValidator(NA_VALUE),
                new CustomFieldValidator(
                    `PFrontSetbackPrincipal Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PFrontSetbackPrincipal',
                            'PFrontSetbackPrincipalMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('PFrontSetbackPrincipalMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PFrontSetbackPrincipal Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PFrontSetbackPrincipal',
                            'PFrontSetbackPrincipalMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL
        }),
        new FieldSettingsModel('PFrontSetbackSecondary', FieldType.NUMBER_NA, MeasurementUnits.FEET, {
            isRequired: true,
            validators: [
                new RequiredFieldValidator(),
                new RangeFieldValidator(NA_VALUE),
                new CustomFieldValidator(
                    `PFrontSetbackSecondary Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PFrontSetbackSecondary',
                            'PFrontSetbackSecondaryMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('PFrontSetbackSecondaryMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PFrontSetbackSecondary Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PFrontSetbackSecondary',
                            'PFrontSetbackSecondaryMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL
        }),
        new FieldSettingsModel('PWaterSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_PRINCIPAL
        }),
        new FieldSettingsModel('PSideSetback', FieldType.NUMBER_NA, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(NA_VALUE),
                new RequiredFieldValidator(),
                new CustomFieldValidator(
                    `PSideSetback Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PSideSetback', 'PSideSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL,
            isRequired: true,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('PSideSetbackMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PSideSetback Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PSideSetback', 'PSideSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL
        }),
        new FieldSettingsModel('PRearSetback', FieldType.NUMBER_NA, MeasurementUnits.FEET, {
            isRequired: true,
            validators: [
                new RequiredFieldValidator(),
                new RangeFieldValidator(NA_VALUE),
                new CustomFieldValidator(
                    `PRearSetback Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PRearSetback', 'PRearSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL,
            showFieldLevelNAButton: true
        }),
        new FieldSettingsModel('PRearSetbackMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PRearSetback Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PRearSetback', 'PRearSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PRINCIPAL
        }),

        // Setbacks - Outbuilding Section
        new FieldSettingsModel('OFrontSetbackPrincipal', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_OUTBUILDING
        }),
        new FieldSettingsModel('OSideSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_OUTBUILDING
        }),
        new FieldSettingsModel('OSideSetbackAtCorner', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_OUTBUILDING
        }),
        new FieldSettingsModel('ORearSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_OUTBUILDING
        }),

        // Setbacks - Tower Section
        new FieldSettingsModel('TFrontSetbackPrincipal', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TFrontSetbackPrincipal Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'TFrontSetbackPrincipal',
                            'TFrontSetbackPrincipalMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TFrontSetbackPrincipalMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TFrontSetbackPrincipal Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'TFrontSetbackPrincipal',
                            'TFrontSetbackPrincipalMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TFrontSetbackSecondary', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TFrontSetbackSecondary Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'TFrontSetbackSecondary',
                            'TFrontSetbackSecondaryMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TFrontSetbackSecondaryMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TFrontSetbackSecondary Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'TFrontSetbackSecondary',
                            'TFrontSetbackSecondaryMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TWaterSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TSideSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TSideSetback Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'TSideSetback', 'TSideSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TSideSetbackMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TSideSetback Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'TSideSetback', 'TSideSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TRearSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TRearSetback Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'TRearSetback', 'TRearSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TRearSetbackMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `TRearSetback Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'TRearSetback', 'TRearSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_TOWER
        }),
        new FieldSettingsModel('TSetbackBetweenTowers', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_TOWER
        }),

        // Setbacks - Penthouse
        new FieldSettingsModel('PeFrontSetbackPrincipal', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeFrontSetbackPrincipal Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PeFrontSetbackPrincipal',
                            'PeFrontSetbackPrincipalMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeFrontSetbackPrincipalMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeFrontSetbackPrincipal Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PeFrontSetbackPrincipal',
                            'PeFrontSetbackPrincipalMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeFrontSetbackSecondary', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeFrontSetbackSecondary Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PeFrontSetbackSecondary',
                            'PeFrontSetbackSecondaryMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeFrontSetbackSecondaryMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeFrontSetbackSecondary Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(
                            currentModelState,
                            'PeFrontSetbackSecondary',
                            'PeFrontSetbackSecondaryMax'
                        );
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeSideSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeSideSetback Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PeSideSetback', 'PeSideSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeSideSetbackMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeSideSetback Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PeSideSetback', 'PeSideSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeRearSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeRearSetback Minimum setback cannot be greater than maximum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PeRearSetback', 'PeRearSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),
        new FieldSettingsModel('PeRearSetbackMax', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [
                new RangeFieldValidator(0),
                new CustomFieldValidator(
                    `PeRearSetback Maximum setback cannot be smaller than minimum setback`,
                    ({ currentModelState }) => {
                        return !isMinGreaterThanMax(currentModelState, 'PeRearSetback', 'PeRearSetbackMax');
                    }
                )
            ],
            section: GeneralSections.SETBACKS_PENTHOUSE
        }),

        // Setbacks - Underground Section
        new FieldSettingsModel('UFrontSetbackPrincipal', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_UNDERGROUND
        }),
        new FieldSettingsModel('UFrontSetbackSecondary', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_UNDERGROUND
        }),
        new FieldSettingsModel('USideSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_UNDERGROUND
        }),
        new FieldSettingsModel('URearSetback', FieldType.NUMBER, MeasurementUnits.FEET, {
            validators: [new RangeFieldValidator(0)],
            section: GeneralSections.SETBACKS_UNDERGROUND
        }),

        // Private Frontages Section
        new FieldSettingsModel('CommonLawn', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('PorchAndFence', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('TerraceOrLightwell', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('Forecourt', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('Stoop', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('Shopfront', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('Gallery', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        }),
        new FieldSettingsModel('Arcade', FieldType.BOOLEAN, MeasurementUnits.NONE, {
            section: GeneralSections.PRIVATE_FRONTAGES
        })
    ];
}

export default { fieldsFactory: calibrationGeneralFieldsFactory, sections } as ITableFieldsConfiguration;
